import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataStore } from '@common/data-models/data-store/data-store';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { PlanningViewsResults } from '@common/data-models/navigation/planning-views-results';
import { AssignmentViewStateRecord } from '@common/data-models/records/assignment-viewstate-record';
import { DraftViewStateRecord } from '@common/data-models/records/draft-viewstate-record';
import { PublicationViewStateRecord } from '@common/data-models/records/publication-viewstate-record';
import { RSSFeedViewStateRecord } from '@common/data-models/records/rss-feed-viewstate-record';
import { RundownListViewStateRecord } from '@common/data-models/records/rundown-list-viewstate-record';
import { SearchViewStateRecord } from '@common/data-models/records/search-viewstate-record';
import { SingleFeedViewStateRecord } from '@common/data-models/records/single-feed-viewstate-record';
import { SingleViewStateRecord } from '@common/data-models/records/single-viewstate-record';
import { TopicViewStateRecord } from '@common/data-models/records/topic-viewstate-record';
import { TwitterFeedViewStateRecord } from '@common/data-models/records/twitter-feed-viewstate-record';
import { ViewStateRecord } from '@common/data-models/records/viewstate-record';
import { WireViewStateRecord } from '@common/data-models/records/wire-viewstate-record';
import { ResultGroupStaticServices } from '@common/data-models/search-results/result-group';
import { ServiceRecord } from '@common/data-models/service/service-record';
import { ServiceResponse } from '@common/data-models/service/service-response';
import { ViewStateTypes } from '@common/enums/viewstate-types';
import { selectCurrentApplication } from '@common/store/navigation/selectors/navigation.selectors';
import { RssFeedCreated, TwitterFeedCreated } from '@discover/data-models/discover-api-object-types/discover-api-object-types';
import { Store } from '@ngrx/store';
import { SingleItemViewStateRecord } from '@src/suite/common/data-models/records/single-item-viewstate-record';
import { StoryViewStateRecord } from '@src/suite/common/data-models/records/story-viewstate-record';
import { from, Observable, Subject, switchMap, takeUntil, tap } from 'rxjs';

import { BasicContentService } from './basic-content-service';
import { CustomCardFieldsService } from './custom-card-fields-service';
import { GlobalTablesService } from './global-tables-service';
import { LanguageSettings } from './language-settings';
import { NomWebServicesJson } from './nom-web-services-json';
import { PublicationChannelsService } from './publication-channels-service';
import { VisualStateService } from './visual-state-service';

export type ViewStateRecordsAlias =
    | ViewStateRecord
    | TopicViewStateRecord
    | AssignmentViewStateRecord
    | StoryViewStateRecord
    | SearchViewStateRecord
    | SingleItemViewStateRecord
    | WireViewStateRecord
    | DraftViewStateRecord
    | RundownListViewStateRecord
    | PublicationViewStateRecord
    | SingleFeedViewStateRecord
    | RSSFeedViewStateRecord;

@Injectable({ providedIn: 'root' })
export class LayoutState {
    destroyed$: Subject<void> = new Subject<void>();
    private _application: string = null;
    application$ = this.store
        .select(selectCurrentApplication)
        .pipe(
            tap(application => (this._application = application)),
            takeUntil(this.destroyed$)
        )
        .subscribe();

    constructor(
        private nomWebServicesJson: NomWebServicesJson,
        public languageSettings: LanguageSettings,
        public globalTablesService: GlobalTablesService,
        public basicContentService: BasicContentService,
        public customCardFieldsService: CustomCardFieldsService,
        public publicationChannelsService: PublicationChannelsService,
        private visualStateService: VisualStateService,
        private store: Store
    ) {
        ResultGroupStaticServices.viewStateService = this;
    }

    // Helper methods

    public async updateTeamCustomCardFields(fields: Array<FieldDef>, team: string): Promise<ServiceResponse<boolean>> {
        return this.customCardFieldsService.updateTeamCustomCardFields(fields, team);
    }

    public async createRSSFeed(vs: RSSFeedViewStateRecord): Promise<ServiceResponse<boolean>> {
        try {
            const response = await this.nomWebServicesJson.discoverAPIPost(
                `${this.nomWebServicesJson.DiscoverAPIBaseUrl}/rss-feeds`,
                {
                    link: vs.viewStateFeedURL,
                    scope: vs.viewStateScope,
                    name: vs.title,
                },
                {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.nomWebServicesJson.token,
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                }
            );

            const feed: RssFeedCreated = response.body as RssFeedCreated;

            if (feed) {
                vs.viewStateFeedID = feed.feedId;
            } else {
                vs.viewStateFeedID = '';
            }
        } catch (error) {
            console.log(error);
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to create rss feed!',
                detailMessage: `Failed to create rss feed! \n\rStackTrace: LayoutState.createRSSFeed(); \n\rError: ${error}`,
            });
            return new ServiceResponse<boolean>(false, false);
        }
        return new ServiceResponse<boolean>(true, true);
    }

    public async createTwitterFeed(vs: TwitterFeedViewStateRecord, credentialsId: string): Promise<ServiceResponse<boolean>> {
        try {
            const feedNames: Array<string> = [];
            const feedIds: Array<string> = [];

            if (vs.viewStateFeedNames.length > 0) {
                const viewStateFeedNames = JSON.parse(vs.viewStateFeedNames);

                for (let i = 0; i < viewStateFeedNames.length; i++) {
                    let twitterName = viewStateFeedNames[i];
                    feedNames.push(twitterName);
                    twitterName = twitterName.charAt(0) === '@' ? twitterName.slice(1) : twitterName;

                    const response = await this.nomWebServicesJson.discoverAPIPost(
                        `${this.nomWebServicesJson.DiscoverAPIBaseUrl}/twitter-feeds`,
                        {
                            feedSetting: twitterName,
                            scope: vs.viewStateScope,
                            name: vs.title + ' - ' + twitterName,
                            wireCredentialId: credentialsId,
                        },
                        {
                            headers: new HttpHeaders({
                                'Content-Type': 'application/json',
                                Authorization: this.nomWebServicesJson.token,
                                accept: 'text/plain',
                            }),
                            responseType: 'json',
                            observe: 'response',
                        }
                    );

                    const feed: TwitterFeedCreated = response.body as TwitterFeedCreated;
                    // this logic supports creation of only one twitter feed at once
                    // TODO: Add logic for creation of multiple feeds at once
                    // TODO: Add logic to check is there any errors in errors array of the feed
                    if (feed.success.length > 0) {
                        feedIds.push(feed.success[0].twitterId);
                    }
                }
                vs.viewStateFeedID = JSON.stringify(feedIds);
            }
        } catch (error) {
            console.log(error);
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to create twitter feed!',
                detailMessage: `Failed to create twitter feed! \n\rStackTrace: LayoutState.createTwitterFeed(); \n\rError: ${error}`,
            });
            return new ServiceResponse<boolean>(false, false);
        }
        return new ServiceResponse<boolean>(true, true);
    }

    public async deleteFeed(viewStateType: number, feedId: string, scope: number): Promise<ServiceResponse<boolean>> {
        try {
            let url: string = null;
            if (Number(viewStateType) === ViewStateTypes.RSSFEED) {
                url = `/rss-feeds/${feedId}?scope=${scope}`;
            }
            if (Number(viewStateType) === ViewStateTypes.TWITTERFEED) {
                url = `/twitter-feeds/${feedId}?scope=${scope}`;
            }
            if (url) {
                const response = await this.nomWebServicesJson.discoverAPIDelete(this.nomWebServicesJson.DiscoverAPIBaseUrl + url, {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json',
                        Authorization: this.nomWebServicesJson.token,
                        accept: 'text/plain',
                    }),
                    responseType: 'json',
                    observe: 'response',
                });

                if (response.ok) {
                    return new ServiceResponse<boolean>(true, true);
                }
            }

            return new ServiceResponse<boolean>(true, true);
        } catch (error) {
            console.log(error);
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to delete feed!',
                detailMessage: `Failed to delete feed! \n\rStackTrace: \n\rError: ${error.message}`,
            });
            return new ServiceResponse<boolean>(false, false);
        }
    }

    public async createViewStateRecord(vs: ViewStateRecord, path?: string): Promise<ServiceResponse<ViewStateRecord>> {
        vs.owner = this.nomWebServicesJson.UserId;
        const database = this.nomWebServicesJson.getDatabase();
        const data = this.nomWebServicesJson.convertor.serializeOld(vs);
        if (vs instanceof SingleViewStateRecord && vs.isViewStateCustomCardItemsBlank) {
            delete data[PSTFieldNames.PSTViewStateCardCustomItems];
        }
        delete data.guid;
        delete data.Guid;

        return this.basicContentService.createBasicContentOld<ViewStateRecord>(database, path || vs.path.fullPath, false, data, null);
    }

    public async updateViewStateRecord(vs: ViewStateRecord): Promise<ServiceResponse<ViewStateRecord>> {
        vs.owner = this.nomWebServicesJson.UserId;
        const database = this.nomWebServicesJson.getDatabase();
        const { guid } = vs;
        const data = this.nomWebServicesJson.convertor.serializeOld(vs, true);
        if (vs instanceof SingleViewStateRecord && vs.isViewStateCustomCardItemsBlank) {
            delete data[PSTFieldNames.PSTViewStateCardCustomItems];
        }
        return await this.basicContentService.updateBasicContentOld<ViewStateRecord>(database, vs.path.fullPath, guid, false, data, false, null, vs);
    }

    public deleteViewStateRecord(vs: ViewStateRecord): Observable<ServiceResponse<RemoveRecordResponse>> {
        return from(this.basicContentService.deleteBasicContent(vs.database, vs.path.fullPath, vs.guid, vs.type));
    }

    public deleteViewStateDiscoverRecord(vs: ViewStateRecord): Observable<ServiceResponse<RemoveRecordResponse>> {
        return from(this.deleteFeed(vs.data.PSTViewStateType, vs.data.PSTViewStateFeedID, vs.data.PSTViewStateFeedScope)).pipe(
            switchMap(res => {
                if (!res.status) {
                    console.log(`Failed to delete feed! \n\rStackTrace: \n\rError: ${res.message}`);
                }
                return from(this.basicContentService.deleteBasicContent(vs.database, vs.path.fullPath, vs.guid, vs.type));
            })
        );
    }

    protected getViewStatesParams(team?: string): any {
        let nomLocations = [];

        if (team === 'all') {
            const teams = this.globalTablesService.getListItemsForTeamLocations();
            nomLocations.push({
                BasePath: `P_${this.globalTablesService.DefaultPlaybookTeam.ID}\\`,
                SearchWIP: true,
                SearchArchives: false,
                SearchTrash: false,
            });
            teams.forEach(currTeam => {
                nomLocations.push({
                    BasePath: `P_${currTeam.id}\\`,
                    SearchWIP: true,
                    SearchArchives: false,
                    SearchTrash: false,
                });
            });
        } else if (team === 'group') {
            const teams = this.globalTablesService.getListItemsForTeamLocations();
            nomLocations.push({
                BasePath: `P_${this.globalTablesService.DefaultPlaybookTeam.ID}\\`,
                SearchWIP: true,
                SearchArchives: false,
                SearchTrash: false,
            });
            teams.forEach(currTeam => {
                if (currTeam.id !== this.nomWebServicesJson.HelloResults.HomeProgram) return;
                nomLocations.push({
                    BasePath: `P_${currTeam.id}\\`,
                    SearchWIP: true,
                    SearchArchives: false,
                    SearchTrash: false,
                });
            });
        } else {
            nomLocations = [
                {
                    BasePath: team ? `P_${team}\\` : `P_${this.nomWebServicesJson.HelloResults.HomeProgram}\\`,
                    SearchWIP: true,
                    SearchArchives: false,
                    SearchTrash: false,
                },
            ];
        }

        // In edit team settings mode don't load personal views
        nomLocations.push({
            BasePath: `U_${this.nomWebServicesJson.UserId}\\`,
            SearchWIP: true,
            SearchArchives: false,
            SearchTrash: false,
        });

        const params = {
            Database: this.nomWebServicesJson.PSTServer,
            QueryTerms: '',
            zFields: null,
            NOMLocations: nomLocations,
            NOMContentTypes: {
                All: true,
                Audio: false,
                Contacts: false,
                Grids: false,
                Messages: false,
                NewsgatheringGrids: false,
                NewsgatheringItems: false,
                Pictures: false,
                Readins: false,
                Rundowns: false,
                Scripts: false,
                Storyboards: false,
                Video: false,
            },
            NOMContentDates: {
                All: true,
                CustomDateRange: false,
                FortyEightHours: false,
                Future: false,
                ItemDateFrom: '1800-02-28T22:00:00.000Z',
                ItemDateTo: '1800-02-28T22:00:00.000Z',
                OneHour: false,
                OneWeek: false,
                TwentyFourHours: false,
                TwoHours: false,
            },
            WireSearchParameters: {
                SearchWires: false,
                Categories: [],
                Providers: [],
                DateOptions: {
                    All: true,
                    CustomDateRange: false,
                    FortyEightHours: false,
                    Future: false,
                    ItemDateFrom: '1800-02-28T22:00:00.000Z',
                    ItemDateTo: '1800-02-28T22:00:00.000Z',
                    OneHour: false,
                    OneWeek: false,
                    TwentyFourHours: false,
                    TwoHours: false,
                },
                ExtendedWireBriefing: true,
            },
            MaxRows: 300,
            SortByRank: false,
            ExactMatch: false,
            FieldsToExclude: null,
            FieldsToReturn: null,
        };

        return params;
    }

    private getViewStateTypesByApplication(applicationName?: string): Array<number> {
        applicationName = applicationName || this._application;
        if (applicationName) {
            if (applicationName === 'playbook') {
                return [ViewStateTypes.TOPIC, ViewStateTypes.ASSIGNMENT, ViewStateTypes.SCRIPT, ViewStateTypes.SEARCH];
            }
            if (applicationName === 'discover') {
                return [ViewStateTypes.RSSFEED, ViewStateTypes.TWITTERFEED, ViewStateTypes.WIREFEED, ViewStateTypes.APFEED, ViewStateTypes.FTPFEED, ViewStateTypes.MULTIPLEFEED];
            }
            if (applicationName === 'journal') {
                return [ViewStateTypes.DRAFT, ViewStateTypes.DRAFTSEARCH];
            }
            if (applicationName === 'live') {
                return [ViewStateTypes.ROLIST, ViewStateTypes.LIVESEARCH];
            }
            if (applicationName === 'cast') {
                return [ViewStateTypes.PUBLICATION, ViewStateTypes.CASTSEARCH];
            }
        }
        return [];
    }

    public async getViewStateRecord(guid: string): Promise<ServiceResponse<ViewStateRecord>> {
        const zFields: Array<{ FieldName: string; FieldValue: any }> = [
            { FieldName: 'guid', FieldValue: guid },
            { FieldName: 'type', FieldValue: 139 },
        ];

        const params = this.getViewStatesParams();
        params.zFields = zFields;
        params.FieldsToExclude = ['PSTViewStateScreenshot', 'backfields'];

        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.post('/api/Search', params, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to load view state!',
                detailMessage: `Failed to load view state! \n\rStackTrace: LayoutState.getViewStateRecord(); \n\rError: ${error}`,
            });
            return new ServiceResponse<ViewStateRecord>(false, null);
        }

        if (response) {
            const responseJSON = response.body;
            if (responseJSON.SearchResults && responseJSON.SearchResults.length > 0) {
                const serviceRecord: ServiceRecord = ServiceRecord.instance(responseJSON.SearchResults[0]);
                const viewStateRecord: ViewStateRecord = this.nomWebServicesJson.convertor.fromServiceViewStateRecord(serviceRecord);
                DataStore.set(viewStateRecord);
                return new ServiceResponse<ViewStateRecord>(true, viewStateRecord);
            }
        }
        return new ServiceResponse<ViewStateRecord>(false, null);
    }

    public async getViewStateRecordByGuids(guids: Array<string>): Promise<ServiceResponse<Array<ViewStateRecord>>> {
        const zFields: Array<{ FieldName: string; FieldValue: any }> = [
            { FieldName: 'guid__list__', FieldValue: guids.join(';') },
            { FieldName: 'type', FieldValue: 139 },
        ];

        const params = this.getViewStatesParams('all');
        params.zFields = zFields;
        params.FieldsToExclude = ['PSTViewStateScreenshot', 'backfields'];

        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.post('/api/Search', params, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to load view state!',
                detailMessage: `Failed to load view state! \n\rStackTrace: LayoutState.getViewStateRecord(); \n\rError: ${error}`,
            });
            return new ServiceResponse<Array<ViewStateRecord>>(false, null);
        }

        const viewStates: Array<ViewStateRecord> = [];
        if (response) {
            const responseJSON = response.body;
            if (responseJSON.SearchResults && responseJSON.SearchResults.length > 0) {
                for (let i = 0; i < responseJSON.SearchResults.length; i++) {
                    const serviceRecord: ServiceRecord = ServiceRecord.instance(responseJSON.SearchResults[i]);
                    const viewStateRecord: ViewStateRecord = this.nomWebServicesJson.convertor.fromServiceViewStateRecord(serviceRecord);
                    viewStates.push(viewStateRecord);
                }

                return new ServiceResponse<Array<ViewStateRecord>>(true, viewStates);
            }
        }
        return new ServiceResponse<Array<ViewStateRecord>>(false, null);
    }

    public async getPartialViewStateRecords(applicationName: string): Promise<ServiceResponse<PlanningViewsResults>> {
        const fieldsToReturn: Array<string> = ['guid', 'Title', 'PSTViewStateType', 'PSTViewStateFilter', 'PSTViewStateSorting'];
        return this.getViewStateRecords(applicationName, fieldsToReturn);
    }

    public async getViewStateRecords(applicationName?: string, fieldsToReturn?: Array<string>, team?: string, filterViewStateType?: number): Promise<ServiceResponse<PlanningViewsResults>> {
        const results: PlanningViewsResults = new PlanningViewsResults();

        applicationName = applicationName || this._application;
        const viewStateTypes: Array<number> = this.getViewStateTypesByApplication(applicationName);
        const viewStateTypesFieldValue = (filterViewStateType && filterViewStateType !== -1) || filterViewStateType === 0 ? filterViewStateType : viewStateTypes.join(';');
        const zFields: Array<{ FieldName: string; FieldValue: any }> = [
            { FieldName: 'virtualPath', FieldValue: `\\\\${this.nomWebServicesJson.PSTServer}` },
            { FieldName: 'type', FieldValue: 139 },
            { FieldName: `${PSTFieldNames.PSTViewStateType}__list__`, FieldValue: viewStateTypesFieldValue },
        ];

        const params = this.getViewStatesParams(team);

        params.zFields = zFields;
        params.FieldsToExclude = ['PSTViewStateScreenshot', 'backfields'];
        if (fieldsToReturn && fieldsToReturn.length > 0) {
            params.FieldsToReturn = fieldsToReturn;
        }
        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.post('/api/Search', params, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            console.log(error);
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to load planning views!',
                detailMessage: `Failed to load planning views! \n\rStackTrace: NomWebServicesJson.getPlanningViews(); \n\rError: ${error}`,
            });
            return new ServiceResponse<PlanningViewsResults>(true, results);
        }

        if (response) {
            for (const p of response.body.SearchResults) {
                const serviceRecord: ServiceRecord = ServiceRecord.instance(p);
                try {
                    const viewStateRecord: ViewStateRecord = this.nomWebServicesJson.convertor.fromServiceViewStateRecord(serviceRecord);
                    const label = 'PSTWebView';
                    viewStateRecord.title = this.languageSettings.get(`${label}_${serviceRecord.ListData.Path}_${serviceRecord.ListData.Title}`).includes(label)
                        ? serviceRecord.ListData.Title
                        : this.languageSettings.get(`${label}_${serviceRecord.ListData.Path}_${serviceRecord.ListData.Title}`);

                    DataStore.set(viewStateRecord);
                    results.add(viewStateRecord);
                } catch (e) {
                    console.log(serviceRecord);
                    console.log(e);
                }
            }
        }

        const res = new ServiceResponse<PlanningViewsResults>(true, results);
        if (res.status) {
            DataStore.registerViewStateResults(applicationName, res.data);
        }
        return res;
    }

    public async loadViewStateRecords(group?: string, creators?: Array<string>, application?: string) {
        let path;
        if (group === 'all') {
            path = 'all';
        } else if (group === 'global') {
            path = this.globalTablesService.DefaultPlaybookTeam.ID;
        } else if (group === 'personal') {
            path = this.nomWebServicesJson.UserId;
        } else {
            path = group;
        }

        await this.getViewStateRecords(application, undefined, path);

        if (group) {
            let res = DataStore.getViewStateResults(this._application)?.views;
            if (res) res = this.filterViewStateResults(group, creators, res);
            DataStore.getViewStateResults(this._application).views = res;

            res = DataStore.getViewStateResults(this._application)?.searches;
            if (res) res = this.filterViewStateResults(group, creators, res);
            DataStore.getViewStateResults(this._application).searches = res;

            res = DataStore.getViewStateResults(this._application)?.multiviews;
            if (res) res = this.filterViewStateResults(group, creators, res);
            DataStore.getViewStateResults(this._application).multiviews = res;
        }
    }

    private filterViewStateResults(group: string, creators: Array<string>, data: ViewStateRecord[]): ViewStateRecord[] {
        const res = data.filter((vs: ViewStateRecord) => {
            if (creators && creators.length) {
                if (creators.indexOf(vs.owner) < 0) return false;
            }
            if (group === 'all') {
                return true;
            }
            if (group === 'global') {
                return vs.path.groupId === this.globalTablesService.DefaultPlaybookTeam.ID;
            }
            if (group === 'personal') {
                return vs.path.groupId === this.nomWebServicesJson.UserId;
            }
            return vs.path.groupId === group;
        });
        return res;
    }
}
